// src/components/Navbar.jsx
import React, { useState, useEffect, useRef, useContext } from 'react';
import { FiHome, FiMenu } from 'react-icons/fi';
import { FaBell, FaUserCircle } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { SidebarContext } from './SidebarContext';
import LogoutButton from './logoutbtn';
import logoimg from '../images/logo.png';
import ApiURL from '../Api/Api';
import { getStoredCompanyBranding, resolveLogoUrl, storeCompanyBranding } from '../utils/companyTheme';

const Navbar = () => {
  const { toggleSidebar } = useContext(SidebarContext);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [readNotificationIds, setReadNotificationIds] = useState([]);
  const [username, setUsername] = useState('Guest');
  const [branding, setBranding] = useState(getStoredCompanyBranding);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const storedUsername = localStorage.getItem('user_name');
    if (storedUsername) setUsername(storedUsername);
    const userId = localStorage.getItem('id') || 'guest';
    try {
      setReadNotificationIds(JSON.parse(localStorage.getItem(`readNotificationIds_${userId}`) || '[]'));
    } catch {
      setReadNotificationIds([]);
    }
  }, []);

  useEffect(() => {
    const syncBranding = () => setBranding(getStoredCompanyBranding());
    window.addEventListener('company-branding-updated', syncBranding);
    window.addEventListener('storage', syncBranding);
    return () => {
      window.removeEventListener('company-branding-updated', syncBranding);
      window.removeEventListener('storage', syncBranding);
    };
  }, []);

  useEffect(() => {
    const userId = localStorage.getItem('id');
    const companyId = localStorage.getItem('company_id');
    const role = localStorage.getItem('role') || '';
    if (!userId || !companyId || role === 'super_admin') return undefined;

    const fetchBranding = async () => {
      try {
        const response = await fetch(`${ApiURL}/getCompanyBranding.php?user_id=${encodeURIComponent(userId)}&company_id=${encodeURIComponent(companyId)}`);
        const data = await response.json();
        if (data.status === 'success' && data.company_branding) {
          const next = storeCompanyBranding(data.company_branding);
          setBranding(next);
        }
      } catch (error) {
        console.error('Failed to refresh company branding:', error);
      }
    };

    fetchBranding();
    const interval = setInterval(fetchBranding, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const userId = localStorage.getItem('id');
    const role = localStorage.getItem('role') || '';
    if (!userId || !role) return undefined;

    const fetchNotifications = async () => {
      try {
        const response = await fetch(`${ApiURL}/getNotifications.php?user_id=${encodeURIComponent(userId)}&role=${encodeURIComponent(role)}`);
        const data = await response.json();
        if (data.status === 'success') {
          setNotifications(Array.isArray(data.notifications) ? data.notifications : []);
        }
      } catch (error) {
        console.error('Failed to load notifications:', error);
      }
    };

    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
        setNotificationOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const goToDashboard = () => {
    navigate('/dashboard');
    setDropdownOpen(false);
  };

  const openNotification = (notification) => {
    const userId = localStorage.getItem('id') || 'guest';
    const nextRead = [...new Set([...readNotificationIds, notification.id])];
    setReadNotificationIds(nextRead);
    localStorage.setItem(`readNotificationIds_${userId}`, JSON.stringify(nextRead));
    setNotifications((prev) => prev.filter((item) => item.id !== notification.id));
    setNotificationOpen(false);
    if (notification?.link) {
      navigate(notification.link);
    }
  };

  const unreadNotifications = notifications.filter((item) => !readNotificationIds.includes(item.id));
  const companyLogo = resolveLogoUrl(branding.logo_path) || logoimg;
  const companyName = branding.company_name || localStorage.getItem('company_name') || 'A2B Transaction';

  return (
    <nav className="fixed top-0 left-0 right-0 h-16 bg-white/95 text-slate-900 shadow-sm z-50 border-b border-slate-200 backdrop-blur-xl">

      <div className="h-full px-3 sm:px-5 lg:px-6 flex items-center justify-between">

        {/* LEFT SECTION */}
        <div className="flex items-center gap-3">

          <button
            className="md:hidden inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-slate-50 text-slate-700 hover:bg-white transition"
            onClick={toggleSidebar}
            aria-label="Toggle sidebar"
          >
            <FiMenu size={22} />
          </button>

          {/* LOGO */}
          <div
            className="flex h-11 w-11 cursor-pointer items-center justify-center overflow-hidden rounded-xl border border-slate-200 bg-white"
            onClick={() => navigate('/dashboard')}
          >
            <img src={companyLogo} alt="Company Logo" className="h-full w-full object-contain p-1" />
          </div>
          <div className="hidden sm:block">
            <p className="max-w-[180px] truncate text-sm font-extrabold leading-tight text-slate-950">{companyName}</p>
            <p className="text-[11px] font-medium text-slate-500">Finance operations</p>
          </div>
        </div>

        {/* CENTER TITLE */}
        <div className="pointer-events-none absolute left-1/2 hidden -translate-x-1/2 lg:block">
          <h1 className="max-w-[360px] truncate text-sm font-bold uppercase tracking-[0.18em] text-slate-400">
            {companyName}
          </h1>
        </div>

        {/* RIGHT SECTION */}
        <div className="flex items-center gap-2 sm:gap-3 relative" ref={dropdownRef}>

          <span className="hidden md:inline max-w-[180px] truncate text-sm font-semibold text-slate-600">
            Hello, {username}
          </span>

          <button
            onClick={goToDashboard}
            className="inline-flex h-10 items-center gap-2 rounded-xl bg-brand-600 px-3 sm:px-4 text-sm font-bold text-white shadow-sm shadow-brand-600/20 transition hover:bg-brand-700"
            title="Home"
          >
            <FiHome className="h-4 w-4" />
            <span className="hidden sm:inline">Home</span>
          </button>

          <div className="relative">
            <button
              onClick={() => {
                setNotificationOpen((prev) => !prev);
                setDropdownOpen(false);
              }}
              className="relative inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-slate-50 text-slate-600 transition hover:bg-white hover:text-slate-950"
              aria-label="Notifications"
            >
              <FaBell size={18} />
              {unreadNotifications.length > 0 && (
                <span className="absolute -right-2 -top-2 min-w-[18px] rounded-full bg-rose-500 px-1 text-[10px] font-bold leading-[18px] text-white">
                  {unreadNotifications.length > 9 ? '9+' : unreadNotifications.length}
                </span>
              )}
            </button>

            {notificationOpen && (
              <div className="absolute right-0 top-12 w-80 max-w-[calc(100vw-1rem)] overflow-hidden rounded-2xl border border-slate-200 bg-white text-slate-900 shadow-2xl">
                <div className="border-b border-slate-200 bg-slate-50 px-4 py-3">
                  <p className="text-sm font-bold">Notifications</p>
                  <p className="text-[11px] text-slate-500">{unreadNotifications.length} unread updates</p>
                </div>
                <div className="max-h-96 overflow-y-auto">
                  {unreadNotifications.length === 0 ? (
                    <div className="px-4 py-6 text-center text-xs text-slate-500">No notifications yet</div>
                  ) : (
                    unreadNotifications.map((notification) => (
                      <button
                        key={notification.id}
                        type="button"
                        onClick={() => openNotification(notification)}
                        className="block w-full border-b border-slate-100 px-4 py-3 text-left hover:bg-slate-50"
                      >
                        <div className="text-xs font-bold text-slate-900">{notification.title}</div>
                        <div className="mt-1 text-[11px] leading-4 text-slate-600">{notification.message}</div>
                        {notification.created_at ? (
                          <div className="mt-1 text-[10px] text-slate-500">{notification.created_at}</div>
                        ) : null}
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          <button
            onClick={() => {
              setDropdownOpen(!dropdownOpen);
              setNotificationOpen(false);
            }}
            className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-slate-50 text-slate-600 transition hover:bg-white hover:text-slate-950"
            aria-label="User menu"
          >
            <FaUserCircle size={22} />
          </button>

          {dropdownOpen && (
            <div className="absolute right-0 top-12 w-40 overflow-hidden rounded-2xl border border-slate-200 bg-white py-2 text-slate-900 shadow-2xl">
              <LogoutButton />
            </div>
          )}

        </div>
      </div>
    </nav>
  );
};

export default Navbar;
